import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  desktopNotificationsActive,
  playNotificationSound,
} from "../../../components/helpdesk/desktopNotificationsPreference";
import { toast } from "../../../utils/toast";

// ─── Avisos de escritorio de la bandeja ──────────────────────────────────────
// Se alimenta del `new_message` del SSE (lo llama useHelpdeskInbox). Dos
// motivos para avisar:
//   · mención: una nota privada que nombra al usuario actual → SIEMPRE, aunque
//     tenga esa conversación abierta y a la vista (la nota puede quedar arriba
//     del hilo y pasar inadvertida);
//   · mensaje: el contacto escribió (sender_role 'user', no eco del teléfono)
//     y la pestaña está oculta o esa conversación no es la abierta.
// Nunca por grupos ni por los números dedicados a notificaciones (esos llegan
// por su propio evento SSE y ni siquiera pasan por acá), ni por el canal
// interno del equipo.
// Con los avisos de escritorio apagados (lo normal hasta que el usuario los
// activa) la MENCIÓN igual avisa, dentro de la app: si no, la nota que pide
// ayuda a alguien pasaría inadvertida. Un mensaje del contacto ya se ve en la
// bandeja, así que sin escritorio no avisa.

const PREVIEW_MAX = 140;
const SEEN_MAX = 300;

const MEDIA_LABELS = {
  image: "Imagen",
  audio: "Audio",
  voice_note: "Nota de voz",
  video: "Video",
  document: "Documento",
  sticker: "Sticker",
  location: "Ubicación",
};

function isGroupMessage(msg, row) {
  if (row?.is_group) return true;
  // En grupos el backend puebla el participante; en un DM nunca.
  return !!(msg?.participant_phone || msg?.participant_lid);
}

/**
 * Decide si un `new_message` merece aviso. Pura (exportada para test).
 * @param {object} msg
 * @param {{userId?:string|null, activeId?:string|null, hidden?:boolean, row?:object|null}} ctx
 * @returns {'mention'|'message'|null}
 */
export function shouldNotify(msg, { userId, activeId, hidden, row } = {}) {
  if (!msg || !msg.conversation_id) return null;
  if (row?.channel === "internal" || row?.channel === "ai") return null;

  const meta = msg.metadata || {};
  if (msg.sender_role === "system") {
    if (meta.kind !== "private_note" || !userId) return null;
    const mentions = Array.isArray(meta.mentions) ? meta.mentions : [];
    if (!mentions.includes(userId)) return null;
    // Mencionarse a uno mismo no es un aviso.
    if ((meta.author_id || msg.sender_id) === userId) return null;
    return "mention";
  }

  if (msg.sender_role !== "user" || msg.from_me) return null;
  if (isGroupMessage(msg, row)) return null;
  if (!hidden && msg.conversation_id === activeId) return null;
  return "message";
}

function preview(msg, t) {
  const text = String(msg?.content || "").replace(/\s+/g, " ").trim();
  if (text) return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX - 1)}…` : text;
  const media = MEDIA_LABELS[msg?.content_type];
  return media ? t(media) : t("Mensaje nuevo");
}

/**
 * @param {object} options
 * @param {string|null|undefined} options.userId          usuario de la sesión
 * @param {() => string|null} options.getActiveId          conversación abierta
 * @param {(id:string) => object|null} options.getRow       fila cargada de la lista
 * @param {(ref:{id:string, channel?:string}) => void} options.onOpen
 * @param {(id:string) => string} [options.getDisplayName]
 * @returns {(msg:object) => void} notify (referencia estable)
 */
export function useDesktopNotifications({ userId, getActiveId, getRow, onOpen, getDisplayName }) {
  const { t } = useTranslation();
  // Último valor de cada dependencia, leído desde la función estable.
  const latest = useRef({ userId, getActiveId, getRow, onOpen, getDisplayName, t });
  useEffect(() => {
    latest.current = { userId, getActiveId, getRow, onOpen, getDisplayName, t };
  });
  // El mismo mensaje puede llegar dos veces (dos streams, reconexión).
  const seenRef = useRef(new Set());
  const openRef = useRef(new Set());

  // Al desmontar la bandeja se cierran los avisos que siguen en pantalla: sus
  // clics apuntarían a una vista que ya no existe.
  useEffect(() => {
    const open = openRef.current;
    return () => {
      open.forEach((n) => {
        try {
          n.close();
        } catch {
          // ya cerrado
        }
      });
      open.clear();
    };
  }, []);

  return useCallback((msg) => {
    const ctx = latest.current;
    const row = msg?.conversation_id ? ctx.getRow?.(msg.conversation_id) ?? null : null;
    const kind = shouldNotify(msg, {
      userId: ctx.userId,
      activeId: ctx.getActiveId?.() ?? null,
      hidden: typeof document !== "undefined" && document.hidden,
      row,
    });
    if (!kind) return;
    const desktop = desktopNotificationsActive();
    if (!desktop && kind !== "mention") return;

    const seen = seenRef.current;
    const key = msg.id || `${msg.conversation_id}:${msg.created_at || msg.createdAt || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (seen.size > SEEN_MAX) seen.delete(seen.values().next().value);

    const { t: tr } = ctx;
    const contact =
      row?.contact_name || msg.sender_name || row?.contact_phone || tr("Contacto");
    let title;
    if (kind === "mention") {
      const meta = msg.metadata || {};
      const resolved = meta.author_id && ctx.getDisplayName ? ctx.getDisplayName(meta.author_id) : "";
      const author =
        meta.author_name ||
        msg.sender_name ||
        (resolved && resolved !== meta.author_id ? resolved : tr("Alguien del equipo"));
      title = tr("{{autor}} te mencionó · {{contacto}}")
        .replace("{{autor}}", author)
        .replace("{{contacto}}", row?.contact_name || row?.contact_phone || tr("conversación"));
    } else {
      title = contact;
    }

    if (!desktop) {
      toast.info(`${title}: ${preview(msg, tr)}`, {
        toastId: `mention-${key}`,
        onClick: () => latest.current.onOpen?.({ id: msg.conversation_id, channel: row?.channel }),
      });
      return;
    }

    try {
      const notification = new window.Notification(title, {
        body: preview(msg, tr),
        // Una ráfaga de la misma conversación reemplaza el aviso en vez de apilar.
        tag: `${kind}-${msg.conversation_id}`,
      });
      openRef.current.add(notification);
      notification.onclose = () => openRef.current.delete(notification);
      notification.onclick = () => {
        try {
          window.focus();
        } catch {
          // sin foco programático
        }
        latest.current.onOpen?.({ id: msg.conversation_id, channel: row?.channel });
        notification.close();
      };
    } catch (err) {
      // Android Chrome exige un Service Worker para `new Notification` y lanza
      // TypeError: ahí el sonido es el único aviso.
      console.warn("Aviso de escritorio no disponible:", err?.message);
    }
    playNotificationSound();
  }, []);
}
